import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Building2, Globe, Save, Bell, Shield, FileText, CalendarClock, TicketCheck, CreditCard, MapPin, Loader2, CheckCircle2, AlertTriangle, Trash2, Mail, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DepartmentsAdmin } from '../components/settings/DepartmentsAdmin';
import { DesignationsAdmin } from '../components/settings/DesignationsAdmin';
import { LookupsAdmin } from '../components/settings/LookupsAdmin';
import { ExpenseCategoriesAdmin } from '../components/settings/ExpenseCategoriesAdmin';
import { AppLiveEmailCard } from '../components/settings/AppLiveEmailCard';
import { CompanyLogoCard } from '../components/settings/CompanyLogoCard';
import { AmcQuoteDefaultsCard } from '../components/settings/AmcQuoteDefaultsCard';
import { SmbOutreachAutomationToggle } from '../components/settings/SmbOutreachAutomationToggle';
import { SmbOutreachThrottleCard } from '../components/settings/SmbOutreachThrottleCard';
import { SmbAutoReplyConfigCard } from '../components/settings/SmbAutoReplyConfigCard';
import { AttendanceGapSyncCard } from '../components/settings/AttendanceGapSyncCard';
import { PageSizeSettingsCard } from '../components/settings/PageSizeSettingsCard';
import { RazorpaySettingsCard } from '../components/settings/RazorpaySettingsCard';
import { SmbPackagesCard } from '../components/settings/SmbPackagesCard';
import { SmbActivationTemplatesCard } from '../components/settings/SmbActivationTemplatesCard';
import { SmbCategoriesCard } from '../components/settings/SmbCategoriesCard';
import { DatabaseBackupCard } from '../components/settings/DatabaseBackupCard';
import { useAuth } from '../context/AuthContext';

const NOTIFICATION_CATEGORIES = [
  { key: 'renewals', label: 'Renewal Alerts', description: 'Domain, SSL, hosting, and AMC contract expirations', icon: CalendarClock, color: 'text-amber-500' },
  { key: 'invoices', label: 'Overdue Invoices', description: 'Invoices past their due date', icon: FileText, color: 'text-red-500' },
  { key: 'leaves', label: 'Leave Requests', description: 'Pending employee leave requests', icon: CalendarClock, color: 'text-blue-500' },
  { key: 'tickets', label: 'High Priority Tickets', description: 'Open support tickets marked as high priority', icon: TicketCheck, color: 'text-orange-500' },
  { key: 'payments', label: 'Recent Payments', description: 'Payment received notifications (last 3 days)', icon: CreditCard, color: 'text-emerald-500' },
];

export const SettingsPage = () => {
  const { dangerToolsEnabled, launchEmailBlastEnabled } = useFeatureFlags();
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole && hasRole('super_admin');
  const [company, setCompany] = useState({ name: '', email: '', phone: '', whatsapp: '', address: '', gst: '', website: '', pan: '', state: '' });
  const [general, setGeneral] = useState({ currency: 'INR', timezone: 'Asia/Kolkata', date_format: 'DD/MM/YYYY', financial_year_start: 'April' });
  const [notifPrefs, setNotifPrefs] = useState({ renewals: true, invoices: true, leaves: true, tickets: true, payments: true });
  const [notifLoading, setNotifLoading] = useState(false);
  const [caEmail, setCaEmail] = useState('');
  const [gmapsKey, setGmapsKey] = useState('');
  const [gmapsTestLoading, setGmapsTestLoading] = useState(false);
  const [gmapsTestResult, setGmapsTestResult] = useState(null);
  const [gmapsKeyVisible, setGmapsKeyVisible] = useState(false);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purgeOtp, setPurgeOtp] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpMaskedEmail, setOtpMaskedEmail] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [settingsRes, prefsRes] = await Promise.all([
          api.get('/settings'),
          api.get('/notifications/preferences'),
        ]);
        if (settingsRes.data.success) {
          const d = settingsRes.data.data;
          setCompany({ name: d.company_name || '', email: d.company_email || '', phone: d.company_phone || '', whatsapp: d.company_whatsapp || '', address: d.company_address || '', gst: d.company_gst || '', website: d.company_website || '', pan: d.company_pan || '', state: d.company_state || '' });
          setGeneral({ currency: d.currency || 'INR', timezone: d.timezone || 'Asia/Kolkata', date_format: d.date_format || 'DD/MM/YYYY', financial_year_start: d.financial_year_start || 'April' });
          setCaEmail(d.ca_email || '');
          setGmapsKey(d.google_maps_api_key || '');
        }
        if (prefsRes.data.success) {
          setNotifPrefs(prefsRes.data.data);
        }
      } catch {}
    };
    fetchAll();
  }, []);

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    try {
      await api.put('/settings', {
        company_name: company.name, company_email: company.email, company_phone: company.phone,
        company_whatsapp: company.whatsapp,
        company_address: company.address, company_gst: company.gst, company_website: company.website,
        company_pan: company.pan, company_state: company.state,
      });
      toast.success('Company settings saved');
    } catch { toast.error('Save failed'); }
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    try { await api.put('/settings', general); toast.success('General settings saved'); } catch { toast.error('Save failed'); }
  };

  const handleSaveCaEmail = async (e) => {
    e.preventDefault();
    try { await api.put('/settings', { ca_email: caEmail }); toast.success('CA email saved'); } catch { toast.error('Save failed'); }
  };

  const handleSaveGmapsKey = async (e) => {
    e.preventDefault();
    try { await api.put('/settings', { google_maps_api_key: gmapsKey }); toast.success('Google Maps API key saved'); } catch { toast.error('Save failed'); }
  };

  const handleTestGmapsKey = async () => {
    setGmapsTestLoading(true);
    setGmapsTestResult(null);
    try {
      const res = await api.post('/settings/test-gmaps-key', { api_key: gmapsKey });
      setGmapsTestResult(res.data);
    } catch (err) {
      setGmapsTestResult({ success: false, status: 'ERROR', message: err.response?.data?.detail || 'Request failed' });
    } finally {
      setGmapsTestLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!purgeTarget) return;
    setOtpSending(true);
    try {
      const res = await api.post('/settings/send-purge-otp', { target: purgeTarget.key });
      if (res.data.success) {
        setOtpSent(true);
        setOtpMaskedEmail(res.data.masked_email || '');
        toast.success(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handlePurge = async () => {
    if (!purgeOtp || !purgeTarget) { toast.error('Please enter the OTP'); return; }
    setPurgeLoading(true);
    try {
      const res = await api.post('/settings/purge-data', { otp: purgeOtp, target: purgeTarget.key });
      if (res.data.success) {
        toast.success(res.data.message);
        setPurgeDialogOpen(false);
        setPurgeOtp('');
        setPurgeTarget(null);
        setOtpSent(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purge data');
    } finally {
      setPurgeLoading(false);
    }
  };

  const openPurge = (target) => {
    setPurgeTarget(target);
    setPurgeOtp('');
    setOtpSent(false);
    setOtpMaskedEmail('');
    setPurgeDialogOpen(true);
  };

  const handleToggleNotifPref = async (key, value) => {
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    setNotifLoading(true);
    try {
      await api.put('/notifications/preferences', updated);
      toast.success(`${value ? 'Enabled' : 'Disabled'} ${NOTIFICATION_CATEGORIES.find(c => c.key === key)?.label}`);
    } catch {
      setNotifPrefs(notifPrefs); // revert on error
      toast.error('Failed to update preference');
    } finally {
      setNotifLoading(false);
    }
  };

  const enabledCount = Object.values(notifPrefs).filter(Boolean).length;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="settings-page">
      <div><h1 className="text-2xl font-bold">Settings</h1><p className="text-muted-foreground">Configure system preferences</p></div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" />Company Information</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSaveCompany} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Company Name</Label><Input value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} data-testid="settings-company-name" /></div>
              <div className="space-y-2"><Label>Email</Label><Input value={company.email} onChange={e => setCompany({ ...company, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={company.phone} onChange={e => setCompany({ ...company, phone: e.target.value })} /></div>
              <div className="space-y-2"><Label>WhatsApp Number</Label><Input value={company.whatsapp} onChange={e => setCompany({ ...company, whatsapp: e.target.value })} placeholder="+919876543210" data-testid="settings-company-whatsapp" /></div>
              <div className="space-y-2"><Label>Website</Label><Input value={company.website} onChange={e => setCompany({ ...company, website: e.target.value })} /></div>
              <div className="space-y-2"><Label>GST Number</Label><Input value={company.gst} onChange={e => setCompany({ ...company, gst: e.target.value })} placeholder="e.g. 27ABCFM0433E1ZX" /></div>
              <div className="space-y-2"><Label>PAN Number</Label><Input value={company.pan} onChange={e => setCompany({ ...company, pan: e.target.value })} placeholder="e.g. ABCFM0433E" data-testid="settings-company-pan" /></div>
              <div className="space-y-2"><Label>State</Label><Input value={company.state} onChange={e => setCompany({ ...company, state: e.target.value })} placeholder="e.g. Maharashtra" data-testid="settings-company-state" /></div>
              <div className="space-y-2"><Label>Address</Label><Input value={company.address} onChange={e => setCompany({ ...company, address: e.target.value })} /></div>
            </div>
            <Button type="submit" data-testid="settings-save-company"><Save className="h-4 w-4 mr-2" />Save</Button>
          </form>
        </CardContent>
      </Card>

      <CompanyLogoCard />

      <AmcQuoteDefaultsCard />

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-secondary" />General Settings</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSaveGeneral} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Currency</Label>
                <Select value={general.currency} onValueChange={v => setGeneral({ ...general, currency: v })}><SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="INR">INR (Indian Rupee)</SelectItem><SelectItem value="USD">USD (US Dollar)</SelectItem><SelectItem value="EUR">EUR (Euro)</SelectItem></SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Timezone</Label>
                <Select value={general.timezone} onValueChange={v => setGeneral({ ...general, timezone: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Asia/Kolkata">Asia/Kolkata (IST)</SelectItem><SelectItem value="America/New_York">America/New York (EST)</SelectItem><SelectItem value="Europe/London">Europe/London (GMT)</SelectItem></SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Date Format</Label>
                <Select value={general.date_format} onValueChange={v => setGeneral({ ...general, date_format: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem><SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem><SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem></SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Financial Year Starts</Label>
                <Select value={general.financial_year_start} onValueChange={v => setGeneral({ ...general, financial_year_start: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="January">January</SelectItem><SelectItem value="April">April</SelectItem><SelectItem value="July">July</SelectItem></SelectContent></Select>
              </div>
            </div>
            <Button type="submit" data-testid="settings-save-general"><Save className="h-4 w-4 mr-2" />Save</Button>
          </form>
        </CardContent>
      </Card>

      <DepartmentsAdmin />

      <DesignationsAdmin />

      <ExpenseCategoriesAdmin />

      <LookupsAdmin kind="hosting_provider" />
      <LookupsAdmin kind="domain_registrar" />
      <LookupsAdmin kind="telecom_operator" />

      {launchEmailBlastEnabled && <AppLiveEmailCard />}

      <PageSizeSettingsCard />

      <SmbOutreachAutomationToggle />
      <SmbOutreachThrottleCard />
      <SmbAutoReplyConfigCard />

      <RazorpaySettingsCard />
      <SmbPackagesCard />
      <SmbActivationTemplatesCard />
      <SmbCategoriesCard />

      <AttendanceGapSyncCard />

      {/* Super-admin-only DB backup — kept as the last general card
          before the danger-zone section since it's high-privilege too. */}
      {isSuperAdmin && <DatabaseBackupCard />}

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-blue-500" />GST Filing</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSaveCaEmail} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CA Email Address</Label>
                <Input value={caEmail} onChange={e => setCaEmail(e.target.value)} placeholder="ca@example.com" data-testid="settings-ca-email" />
                <p className="text-xs text-muted-foreground">Used as default recipient when sending GSTR reports. Comma-separated for multiple.</p>
              </div>
            </div>
            <Button type="submit" data-testid="settings-save-ca-email"><Save className="h-4 w-4 mr-2" />Save</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><MapPin className="h-5 w-5 text-rose-500" />Google Maps API</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSaveGmapsKey} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="relative">
                  <Input value={gmapsKey} onChange={e => { setGmapsKey(e.target.value); setGmapsTestResult(null); }} placeholder="AIza..." data-testid="settings-gmaps-key" type={gmapsKeyVisible ? 'text' : 'password'} className="pr-10" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setGmapsKeyVisible(!gmapsKeyVisible)} tabIndex={-1}>
                    {gmapsKeyVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Used for reverse geocoding employee login locations to Area, City, State, Country.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" data-testid="settings-save-gmaps"><Save className="h-4 w-4 mr-2" />Save</Button>
              <Button type="button" variant="outline" onClick={handleTestGmapsKey} disabled={gmapsTestLoading} data-testid="settings-test-gmaps">
                {gmapsTestLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />}
                {gmapsTestLoading ? 'Testing...' : 'Test Key'}
              </Button>
            </div>
            {gmapsTestResult && (
              <div className={`p-4 rounded-lg border ${gmapsTestResult.success ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`} data-testid="gmaps-test-result">
                <div className="flex items-start gap-3">
                  {gmapsTestResult.success
                    ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    : <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  }
                  <div className="flex-1 min-w-0 space-y-2">
                    <p className={`text-sm font-medium ${gmapsTestResult.success ? 'text-emerald-500' : 'text-red-400'}`}>
                      {gmapsTestResult.success ? 'Key is valid' : `Error: ${gmapsTestResult.status}`}
                    </p>
                    <p className="text-sm text-muted-foreground">{gmapsTestResult.message}</p>
                    {gmapsTestResult.formatted_address && (
                      <div className="text-xs space-y-1 mt-2 pt-2 border-t border-border">
                        <p><span className="text-muted-foreground">Test Coordinates:</span> {gmapsTestResult.test_coordinates}</p>
                        <p><span className="text-muted-foreground">Resolved Address:</span> {gmapsTestResult.formatted_address}</p>
                        {gmapsTestResult.parsed_components && (
                          <p><span className="text-muted-foreground">Parsed:</span> {Object.entries(gmapsTestResult.parsed_components).map(([k,v]) => `${k}: ${v}`).join(' | ')}</p>
                        )}
                      </div>
                    )}
                    {gmapsTestResult.raw_response?.error_message && (
                      <p className="text-xs text-red-400 mt-1 font-mono bg-red-500/5 p-2 rounded">{gmapsTestResult.raw_response.error_message}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Bell className="h-5 w-5 text-amber-500" />Notification Preferences</CardTitle>
              <CardDescription className="mt-1">Choose which notifications appear in your notification panel</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{enabledCount}/{NOTIFICATION_CATEGORIES.length} active</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {NOTIFICATION_CATEGORIES.map(cat => (
              <div key={cat.key} className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${notifPrefs[cat.key] ? 'bg-muted/20 border-border' : 'bg-muted/5 border-transparent opacity-60'}`} data-testid={`notif-pref-${cat.key}`}>
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${notifPrefs[cat.key] ? 'bg-primary/10' : 'bg-muted/30'}`}>
                    <cat.icon className={`h-4.5 w-4.5 ${notifPrefs[cat.key] ? cat.color : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{cat.label}</p>
                    <p className="text-xs text-muted-foreground">{cat.description}</p>
                  </div>
                </div>
                <Switch
                  checked={notifPrefs[cat.key]}
                  onCheckedChange={(v) => handleToggleNotifPref(cat.key, v)}
                  disabled={notifLoading}
                  data-testid={`toggle-${cat.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone — only renders when the global feature flag is ON */}
      {dangerToolsEnabled && (
      <Card className="border-red-500/30">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-red-500"><AlertTriangle className="h-5 w-5" />Danger Zone</CardTitle>
          <CardDescription>Irreversible actions that affect your data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: 'attendance', label: 'Delete All Attendance Data', desc: 'Permanently removes all check-in/check-out records.' },
            { key: 'projects', label: 'Delete All Projects & Related Data', desc: 'Deletes projects and their mapped tasks, invoices, and expenses.' },
            { key: 'tasks', label: 'Delete All Tasks', desc: 'Permanently removes all task records.' },
            { key: 'tickets', label: 'Delete All Tickets', desc: 'Permanently removes all ticket records.' },
            { key: 'invoices', label: 'Delete All Invoices', desc: 'Permanently removes all invoice records.' },
            { key: 'transactions', label: 'Delete All Transactions', desc: 'Permanently removes all transaction records.' },
            { key: 'activity_logs', label: 'Delete Activity Log', desc: 'Permanently removes all activity log entries.' },
            { key: 'eod_notes', label: 'Truncate All EOD Notes', desc: 'Permanently removes every employee\u2019s End-of-Day notes across all dates.' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between p-4 rounded-lg border border-red-500/20 bg-red-500/5">
              <div>
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              <Button variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10 shrink-0" onClick={() => openPurge(item)} data-testid={`purge-${item.key}-btn`}>
                <Trash2 className="h-4 w-4 mr-2" />Purge
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
      )}

      {/* Purge Confirmation Dialog */}
      <Dialog open={purgeDialogOpen} onOpenChange={setPurgeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500"><AlertTriangle className="h-5 w-5" />Confirm Purge</DialogTitle>
            <DialogDescription>{purgeTarget?.label || 'Delete data'}. Verify via email OTP to proceed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-sm text-red-400">
              {purgeTarget?.desc} This action cannot be reversed.
            </div>
            {!otpSent ? (
              <div className="text-center space-y-3">
                <p className="text-sm text-muted-foreground">A 6-digit verification code will be sent to your registered email address.</p>
                <Button onClick={handleSendOtp} disabled={otpSending} className="w-full" data-testid="send-otp-btn">
                  {otpSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  {otpSending ? 'Sending...' : 'Send OTP to Email'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to <span className="font-medium text-foreground">{otpMaskedEmail}</span></p>
                <Input
                  value={purgeOtp}
                  onChange={e => setPurgeOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit OTP"
                  className="text-center text-lg tracking-[0.5em] font-mono"
                  maxLength={6}
                  data-testid="purge-otp-input"
                />
                <button type="button" className="text-xs text-primary hover:underline" onClick={handleSendOtp} disabled={otpSending}>
                  {otpSending ? 'Resending...' : 'Resend OTP'}
                </button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeDialogOpen(false)}>Cancel</Button>
            {otpSent && (
              <Button variant="destructive" onClick={handlePurge} disabled={purgeLoading || purgeOtp.length !== 6} data-testid="purge-confirm-btn">
                {purgeLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete Permanently
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
